import React from "react";

import classNames from "classnames";

import { LoadingSpinner } from "../Loading/LoadingSpinner";

type Size = "small" | "medium" | "large";
export type Variant = "contained" | "outlined" | "text";
export type Color = "primary" | "secondary" | "error";
export type ButtonProps = {
  className?: string;
  size?: Size;
  variant?: Variant;
  color?: Color;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  fullWidth?: boolean;
  spinnerClassName?: string;
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const sizeClasses: { [S in Size]: string } = {
  small: "px-4 py-1 !text-sm  ",
  medium: "px-6 py-2 ",
  large: "px-6 py-2  !text-base",
};
const variantColorClasses: { [V in Variant]: Record<Color, string> } = {
  contained: {
    primary:
      "bg-primary-main border  border-transparent text-primary-contrastText hover:bg-primary-light active:bg-primary-main ",
    secondary:
      "bg-background-main border border-transparent border-primary-main text-background-contrastText hover:bg-background-light active:bg-background-main",
    error:
      "bg-error-main border border-transparent border-primary-main text-error-contrastText hover:bg-error-light active:bg-error-main",
  },
  outlined: {
    primary:
      "text-primary-main border  border-primary-main hover:text-primary-light active:border-primary-main active:text-primary-main hover:border-primary-light  ",
    secondary:
      "text-background-light border  border-background-light hover:text-background-main hover:border-background-main   ",
    error:
      "text-error-main border  border-error-main hover:text-error-dark hover:border-error-dark ",
  },
  text: {
    primary:
      " text-primary-main border border-transparent hover:text-primary-light active:text-primary-main ",
    secondary:
      "text-background-light border border-transparent hover:text-background-main ",
    error: "text-error-main border border-transparent hover:text-error-dark ",
  },
};

export function Button({
  className,
  size = "medium",
  color = "primary",
  variant = "contained",
  disabled = false,
  spinnerClassName,
  children,
  loading = false,
  onClick,
  fullWidth,
  ...rest
}: ButtonProps) {
  const isUnavailable = disabled || loading;

  return (
    <div className={classNames({ "cursor-not-allowed": isUnavailable })}>
      <button
        type="button"
        disabled={isUnavailable}
        aria-busy={loading || undefined}
        className={classNames(
          "relative flex items-center justify-center rounded-xl text-base !font-semibold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main",
          variantColorClasses[variant][color],
          sizeClasses[size],
          className,
          {
            "w-full": fullWidth,
            "cursor-not-allowed select-none opacity-60": isUnavailable,
          },
        )}
        onClick={isUnavailable ? undefined : onClick}
        {...rest}
      >
        {loading && (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center"
          >
            <LoadingSpinner
              className={classNames(spinnerClassName)}
              style={{ height: "1rem", width: "1rem" }}
            />
          </span>
        )}
        <span
          className={classNames(
            {
              "opacity-0": loading,
            },
            "flex items-center",
          )}
        >
          {children || "Click me"}
        </span>
      </button>
    </div>
  );
}
